import { useState, useCallback } from 'react'
import { Copy, Check } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface CopyButtonProps {
  text: string
  size?: 'sm' | 'md'
  variant?: 'icon' | 'button'
  label?: string
  className?: string
  onCopy?: () => void
}

export function CopyButton({
  text,
  size = 'sm',
  variant = 'icon',
  label = 'Copy',
  className = '',
  onCopy,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      onCopy?.()
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [text, onCopy])

  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'
  const buttonPadding = size === 'sm' ? 'p-1' : 'p-1.5'

  if (variant === 'button') {
    return (
      <button
        onClick={handleCopy}
        className={`
          inline-flex items-center gap-1.5
          px-2 py-1
          text-xs
          bg-white/5 hover:bg-white/10
          border border-white/10 hover:border-white/20
          rounded
          text-gray-400 hover:text-white
          transition-all duration-200
          ${className}
        `}
        title={copied ? 'Copied!' : label}
      >
        <AnimatePresence mode="wait">
          {copied ? (
            <motion.span
              key="check"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="text-accent-green"
            >
              <Check className={iconSize} />
            </motion.span>
          ) : (
            <motion.span
              key="copy"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <Copy className={iconSize} />
            </motion.span>
          )}
        </AnimatePresence>
        <span>{copied ? 'Copied!' : label}</span>
      </button>
    )
  }

  return (
    <button
      onClick={handleCopy}
      className={`
        inline-flex items-center justify-center
        ${buttonPadding}
        text-gray-500 hover:text-white
        hover:bg-white/10
        rounded
        transition-all duration-200
        ${className}
      `}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.span
            key="check"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="text-accent-green"
          >
            <Check className={iconSize} />
          </motion.span>
        ) : (
          <motion.span
            key="copy"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <Copy className={iconSize} />
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  )
}

// Inline copy button that appears next to text on hover
interface InlineCopyProps {
  text: string
  children: React.ReactNode
  className?: string
}

export function InlineCopy({ text, children, className = '' }: InlineCopyProps) {
  return (
    <span className={`group inline-flex items-center gap-1 ${className}`}>
      {children}
      <span className="opacity-0 group-hover:opacity-100 transition-opacity">
        <CopyButton text={text} size="sm" />
      </span>
    </span>
  )
}

export default CopyButton
